import React, { useState } from "react";
import { Tooltip } from "antd";
import moment from "moment";
import { useDashboardCount, useDashboardSiteEmployees } from "hooks/dashboard/dashboard";
import { SiteIcon } from "components/icons";

const TONES = ["mint", "violet", "amber", "blue"];

const getInitials = (name) => {
    if (!name) return "?";
    return name.trim().split(/\s+/).slice(0, 2).map((word) => word[0]).join("").toUpperCase();
};

const formatTime = (value) => {
    if (!value) return null;
    const parsed = moment(value, ["YYYY-MM-DD HH:mm:ss", "DD-MM-YYYY hh:mm ss", moment.ISO_8601]);
    return parsed.isValid() ? parsed.format("HH:mm") : value;
};

const SiteCard = ({ site, tone }) => {
    const [isHovered, setIsHovered] = useState(false);

    const { data: siteEmployeeData, isFetching } = useDashboardSiteEmployees(site?.siteId, isHovered);
    const employees = siteEmployeeData?.data;

    const isLoading = isFetching && !employees;
    const columns = !employees?.length ? 1 : employees.length > 12 ? 3 : employees.length > 5 ? 2 : 1;

    const popover = (
        <div className="dash-pop">
            <div className="dash-pop-head">
                <span className="dash-pop-title">
                    <i className="dash-pop-dot" />
                    {site.siteName}
                </span>
                <span className="dash-pop-count">
                    {isLoading ? "Loading" : `${employees?.length || 0} on site`}
                </span>
            </div>

            {isLoading ? (
                <div className="dash-pop-list">
                    {[0, 1, 2].map((row) => (
                        <div key={row} className="dash-pop-row is-skeleton">
                            <span className="dash-pop-avatar" />
                            <span className="dash-pop-info">
                                <span className="dash-pop-bar" />
                                <span className="dash-pop-bar is-short" />
                            </span>
                        </div>
                    ))}
                </div>
            ) : !employees?.length ? (
                <div className="dash-pop-empty">No one is checked in at this site right now.</div>
            ) : (
                <div className={`dash-pop-list is-cols-${columns}`}>
                    {employees.map((employee, index) => (
                        <div key={employee.employeeCode || index} className="dash-pop-row">
                            <span className="dash-pop-avatar">{getInitials(employee.employeeName)}</span>
                            <span className="dash-pop-info">
                                <span className="dash-pop-name">{employee.employeeName || "Unknown"}</span>
                                <span className="dash-pop-meta">
                                    {employee.employeeCode}
                                    {employee.location ? ` · ${employee.location}` : ""}
                                </span>
                            </span>
                            {formatTime(employee.lastKnownTime) && (
                                <span className="dash-pop-time">{formatTime(employee.lastKnownTime)}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <Tooltip
            placement="rightTop"
            overlayClassName={`dash-pop-overlay is-${tone} is-cols-${columns}`}
            visible={isHovered}
            onVisibleChange={setIsHovered}
            title={popover}
        >
            <div className={`dash-site-card is-${tone}`}>
                <span className="dash-site-name">{site.siteName}</span>
                <div className="dash-site-body">
                    <span>
                        <span className="dash-site-label">Total employees</span>
                        <span className="dash-site-value">{site.totalEmployees}</span>
                    </span>
                    <span className="dash-site-icon"><SiteIcon /></span>
                </div>
                <span className="dash-site-foot">Hover to see who is on site</span>
            </div>
        </Tooltip>
    );
};

const DashboardCard = () => {
    const { data: dashboardData } = useDashboardCount();

    const sites = dashboardData?.data?.siteData;

    if (sites && sites.length === 0) {
        return <div className="dash-site-empty">No site attendance to show yet.</div>;
    }

    return (
        <div className="dash-site-grid">
            {sites?.map((item, index) => (
                <SiteCard key={item?.siteId ?? index} site={item} tone={TONES[index % TONES.length]} />
            ))}
        </div>
    );
};

export default DashboardCard;
